import React, { useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box, Typography } from "@mui/material";

/**
 * Shared rich-text editor (Quill, Thunder-themed). Controlled: `value` is HTML,
 * `onChange(html)` fires with sanitized-on-save HTML (backend also sanitizes).
 * Quill represents "empty" as "<p><br></p>" — we normalize that to "".
 *
 * Props: value, onChange, placeholder, minHeight, label, required, error,
 *        helperText, readOnly, compact (fewer toolbar buttons).
 */
const FULL_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "code-block"],
  ["link"],
  ["clean"],
];
const COMPACT_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "clean"],
];

const isEmptyHtml = (html) =>
  !html || html === "<p><br></p>" || html.replace(/<[^>]*>/g, "").trim() === "";

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write here…",
  minHeight = 140,
  label,
  required = false,
  error = false,
  helperText,
  readOnly = false,
  compact = false,
}) {
  const modules = useMemo(
    () => ({ toolbar: compact ? COMPACT_TOOLBAR : FULL_TOOLBAR, clipboard: { matchVisual: false } }),
    [compact]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography
          component="label"
          sx={{ display: "block", mb: 0.5, fontSize: 13, fontWeight: 600, color: error ? "error.main" : "text.secondary" }}
        >
          {label}{required && <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>}
        </Typography>
      )}
      <Box
        sx={{
          "& .ql-toolbar": {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderColor: error ? "#d32f2f" : "#e3e6ee",
            bgcolor: "#fafbfe",
          },
          "& .ql-container": {
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            borderColor: error ? "#d32f2f" : "#e3e6ee",
            fontFamily: "inherit",
            fontSize: "0.9rem",
          },
          "& .ql-editor": { minHeight },
          "& .ql-editor.ql-blank::before": { color: "#9aa0b4", fontStyle: "normal" },
          // Thunder accent on active toolbar buttons
          "& .ql-toolbar .ql-active, & .ql-toolbar button:hover": { color: "#6C50BF" },
          "& .ql-snow .ql-stroke": { stroke: "#5b6072" },
          "& .ql-snow .ql-active .ql-stroke": { stroke: "#6C50BF" },
        }}
      >
        <ReactQuill
          theme="snow"
          readOnly={readOnly}
          value={value || ""}
          onChange={(html) => onChange?.(isEmptyHtml(html) ? "" : html)}
          modules={modules}
          placeholder={placeholder}
        />
      </Box>
      {helperText && (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: error ? "error.main" : "text.secondary" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
